import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getPublicAppointment, cancelPublicAppointment } from '../../services/api';
import { applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDateTimeLong } from '../../utils/formatDateTime';

export default function PublicCancel() {
  const { slug, token } = useParams();
  const confirm = useConfirm();
  const [appt,      setAppt]      = useState(null);
  const [tenant,    setTenant]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    getPublicAppointment(slug, token)
      .then(r => {
        setAppt(r.data.appointment);
        setTenant(r.data.tenant);
        if (r.data.tenant?.primary_color) {
          applyTenantBranding(r.data.tenant.primary_color);
        }
      })
      .catch(e => setError(e.response?.data?.error || 'Agendamento não encontrado'))
      .finally(() => setLoading(false));
    return () => clearTenantBranding();
  }, [slug, token]);

  const onCancel = async () => {
    const ok = await confirm({
      title: 'Cancelar agendamento?',
      message: 'Você está prestes a cancelar este agendamento. O estabelecimento será notificado e o horário voltará a ficar disponível para outros clientes.',
      confirmText: 'Sim, cancelar',
      cancelText: 'Voltar',
      variant: 'danger',
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await cancelPublicAppointment(slug, token);
      setDone(true);
      toast.success('Agendamento cancelado.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cancelar agendamento.');
    } finally { setCancelling(false); }
  };

  const fmtDate = formatDateTimeLong;

  return (
    <div style={{ minHeight:'100vh', background:'var(--page-bg)', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <div style={{ background:'var(--primary)', color:'#fff', padding:'16px 24px', display:'flex', alignItems:'center', gap:12 }}>
        {tenant?.logo_url
          ? <img src={tenant.logo_url} alt="logo" style={{ height:40, objectFit:'contain', borderRadius:6, background:'rgba(255,255,255,.15)', padding:3 }} />
          : <div style={{ width:40, height:40, borderRadius:10, background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700 }}>
              {tenant?.name?.[0] || '?'}
            </div>
        }
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>{tenant?.name || 'Carregando…'}</div>
          <div style={{ fontSize:12, opacity:.85 }}>Cancelamento de agendamento</div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex:1, display:'flex', justifyContent:'center', alignItems:'center', padding:24 }}>
        <div style={{ background:'var(--surface)', borderRadius:16, padding:32, maxWidth:440, width:'100%', boxShadow:'0 4px 24px rgba(0,0,0,.08)' }}>

          {loading && (
            <div style={{ textAlign:'center', padding:32 }}><div className="spinner" /></div>
          )}

          {!loading && error && (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>❌</div>
              <h2 style={{ color:'var(--gray-900)', marginBottom:8 }}>Link inválido</h2>
              <p style={{ color:'var(--gray-500)', fontSize:14 }}>{error}</p>
              <Link to={`/agendar/${slug}`} style={{ display:'inline-block', marginTop:20, color:'var(--primary)', fontSize:13, fontWeight:500, textDecoration:'none' }}>
                ← Fazer novo agendamento
              </Link>
            </div>
          )}

          {!loading && done && (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:56, marginBottom:16 }}>✅</div>
              <h2 style={{ color:'var(--gray-900)', marginBottom:8 }}>Agendamento cancelado</h2>
              <p style={{ color:'var(--gray-500)', fontSize:14, marginBottom:24 }}>
                Seu agendamento foi cancelado com sucesso.
              </p>
              <Link to={`/agendar/${slug}`}
                style={{ display:'inline-block', background:'var(--primary)', color:'#fff', borderRadius:8, padding:'10px 24px', fontSize:14, fontWeight:600, textDecoration:'none' }}>
                Fazer novo agendamento
              </Link>
            </div>
          )}

          {!loading && !error && !done && appt && (
            <>
              {appt.status !== 'scheduled' ? (
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>ℹ️</div>
                  <h2 style={{ color:'var(--gray-900)', marginBottom:8 }}>
                    {appt.status === 'cancelled' ? 'Já cancelado' : 'Não disponível'}
                  </h2>
                  <p style={{ color:'var(--gray-500)', fontSize:14 }}>
                    {appt.status === 'cancelled'
                      ? 'Este agendamento já foi cancelado anteriormente.'
                      : 'Este agendamento não pode ser cancelado pois já foi concluído ou está em outro estado.'}
                  </p>
                  <Link to={`/agendar/${slug}`} style={{ display:'inline-block', marginTop:20, color:'var(--primary)', fontSize:13, fontWeight:500, textDecoration:'none' }}>
                    ← Fazer novo agendamento
                  </Link>
                </div>
              ) : (
                <>
                  <h2 style={{ margin:'0 0 4px', color:'var(--gray-900)', fontSize:20 }}>Cancelar agendamento</h2>
                  <p style={{ color:'var(--gray-500)', fontSize:14, marginBottom:24 }}>Confirme os dados abaixo antes de cancelar.</p>

                  <div style={{ background:'var(--gray-50)', borderRadius:12, padding:'16px 20px', marginBottom:24 }}>
                    {appt.service_name && (
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, fontSize:14 }}>
                        <span style={{ color:'var(--gray-500)' }}>Serviço</span>
                        <span style={{ fontWeight:600, color:'var(--gray-900)' }}>{appt.service_name}</span>
                      </div>
                    )}
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, fontSize:14 }}>
                      <span style={{ color:'var(--gray-500)' }}>Data e horário</span>
                      <span style={{ fontWeight:600, color:'var(--gray-900)', textAlign:'right', maxWidth:'60%' }}>{fmtDate(appt.datetime)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:14 }}>
                      <span style={{ color:'var(--gray-500)' }}>Duração</span>
                      <span style={{ fontWeight:600, color:'var(--gray-900)' }}>{appt.duration_minutes} min</span>
                    </div>
                  </div>

                  <div className="error-box" style={{ marginBottom:20 }}>
                    ⚠️ Esta ação não pode ser desfeita. Para reagendar, crie um novo agendamento.
                  </div>

                  <div style={{ display:'flex', gap:10 }}>
                    <Link to={`/agendar/${slug}`}
                      style={{ flex:1, textAlign:'center', padding:'11px', borderRadius:8, border:'1px solid var(--gray-200)', background:'var(--surface)', color:'var(--gray-700)', fontSize:14, fontWeight:600, textDecoration:'none' }}>
                      Voltar
                    </Link>
                    <button onClick={onCancel} disabled={cancelling}
                      className={`btn btn-danger${cancelling ? ' btn-loading' : ''}`}
                      style={{ flex:1 }}>
                      {cancelling ? '' : 'Cancelar agendamento'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {!tenant?.whitelabel && (
        <div style={{ textAlign:'center', padding:'16px', color:'var(--gray-400)', fontSize:12 }}>
          Powered by <strong style={{ color:'var(--gray-500)' }}>AtendoApp</strong>
        </div>
      )}
    </div>
  );
}
